import { toTitleCase } from 'utils/shared/toTitleCase';
import { useGetCurrentUser } from 'hooks/useGetCurrentUser';
import classNames from 'styles/utils/classNames';
import PaymentStep from './PaymentStep';
import ProfileStep from './ProfileStep';
import ScheduleStep from './ScheduleStep';
import Stepper from './Stepper';
import { OnboardingStepEnum } from '../../props';

const steps = [
  {
    step: OnboardingStepEnum.PROFILE,
    title: 'Account setup',
    stepTitle: 'Create your Listing',
    description: 'Help players understand who you are and what you teach.',
  },
  {
    step: OnboardingStepEnum.SCHEDULE,
    stepTitle: 'Lesson settings',
    title: 'Just a few to steps to get started.',
    description: 'Complete the onboarding to access all the best features. ',
  },
  {
    step: OnboardingStepEnum.PAYMENTS,
    stepTitle: 'Payment details',
    title: 'Account setup',
    description: 'Connect bank account to protect yourself and players from fraud. ',
  },
];

interface OnboardingStepsProps {
  currentStep: OnboardingStepEnum;
  setCurrentStep: (step: OnboardingStepEnum | null) => void;
  coachData?: any;
  isLoading?: boolean;
}

const OnboardingSteps = ({
  currentStep,
  setCurrentStep,
  coachData,
  isLoading,
}: OnboardingStepsProps) => {
  const wrapperShadow = 'shadow-[0px_4px_16px_0px_rgba(0,0,0,0.04)]';
  const user = useGetCurrentUser();

  // Find the current step data
  const currentStepData = steps.find((step) => step.step === currentStep);

  // Helper function to get the next step
  const getNextStep = (currentStep: OnboardingStepEnum): OnboardingStepEnum | null => {
    const currentIndex = steps.findIndex((step) => step.step === currentStep);
    if (currentIndex === -1 || currentIndex === steps.length - 1) {
      return null; // No next step, onboarding complete
    }
    const nextStep = steps[currentIndex + 1].step;
    // check if the next step is payments and if the coach has already set up stripe
    if (nextStep === OnboardingStepEnum.PAYMENTS) {
      if (!!coachData?.stripeMerchantChargesEnabled) {
        return null;
      }
    }
    return nextStep;
  };

  return (
    <>
      <div className={'container mx-auto max-sm:px-4'}>
        <h3 className="typography-product-heading-desktop max-sm:typography-product-heading-mobile mb-6 mt-10 max-sm:my-6">
          Welcome! Coach {toTitleCase(user.user?.fullName || '')}
        </h3>
        <div
          className={classNames(
            'rounded-3xl border border-color-border-card-lightmode bg-color-bg-darkmode-primary',
            wrapperShadow,
          )}
        >
          <div className="rounded-2xl bg-color-bg-darkmode-primary px-20 py-12 max-md:px-ds-md max-md:py-ds-xl">
            <div className="mb-7 flex flex-col gap-ds-sm text-center text-color-text-lightmode-invert">
              <h3 className="typography-product-heading-desktop max-md:typography-product-heading-mobile">
                {currentStepData?.title}
              </h3>
              <p className="typography-product-body-desktop max-md:typography-product-caption text-color-text-darkmode-placeholder">
                {currentStepData?.description}
              </p>
            </div>

            <Stepper steps={steps} currentStep={currentStep} />

            {currentStep === OnboardingStepEnum.PROFILE && (
              <ProfileStep
                onSubmit={() => setCurrentStep(getNextStep(currentStep))}
                coachData={coachData}
                isLoading={isLoading}
              />
            )}
            {currentStep === OnboardingStepEnum.PAYMENTS && (
              <PaymentStep onSkip={() => setCurrentStep(getNextStep(currentStep))} />
            )}
            {currentStep === OnboardingStepEnum.SCHEDULE && (
              <ScheduleStep
                onSubmit={() => setCurrentStep(getNextStep(currentStep))}
                coachData={coachData}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
      {/* hiding for mvp */}
      {/* <OnboardingVideos className="mt-[4.5rem]" /> */}
    </>
  );
};

export default OnboardingSteps;
